/* import __COLOCATED_TEMPLATE__ from './workflow-side-sheet.hbs'; */
/* RESPONSIBLE TEAM: team-ml */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get, post, put, ajaxDelete } from 'embercom/lib/ajax';
import type AppService from 'embercom/services/app-service';
import type { TaskGenerator } from 'ember-concurrency';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import SdrRoutingOutcomeEditorConfig from 'embercom/objects/visual-builder/configuration/editor/sdr-routing-outcome';
import type Store from '@ember-data/store';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

interface Signature {
  Args: {
    onClose: () => void;
    isOpen: boolean;
    routingOutcomeIdentifier: string;
  };
}

export default class SalesDevelopmentWorkflowSideSheet extends Component<Signature> {
  @service declare appService: AppService;
  @service declare store: Store;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked workflow?: Workflow;
  @tracked showConfirmationModal = false;
  @tracked initialWorkflowState?: string;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.fetchWorkflowTask).perform();
  }

  get editorConfiguration() {
    return new SdrRoutingOutcomeEditorConfig({
      workflow: this.workflow,
    });
  }

  get isWorkflowValid() {
    return this.workflow?.validations.isValid ?? false;
  }

  get isSaveDisabled() {
    return !this.isWorkflowValid;
  }

  get hasUnsavedChanges() {
    return !!this.workflow?.hasUnsavedChanges;
  }

  @action
  handleClose() {
    if (this.hasUnsavedChanges) {
      this.showConfirmationModal = true;
      return;
    }
    // If closing an empty workflow without changes, delete it
    if (this._isEmptyWorkflow()) {
      taskFor(this.deleteWorkflowTask).perform();
      return;
    }
    this.args.onClose();
  }

  @action
  confirmClose() {
    this.showConfirmationModal = false;
    if (this.workflow) {
      // If the workflow would be empty after rollback, delete it instead
      if (this._shouldDeleteAfterRollback()) {
        taskFor(this.deleteWorkflowTask).perform();
      } else {
        this.workflow.rollbackAttributes();
        this.args.onClose();
      }
    } else {
      this.args.onClose();
    }
  }

  @action
  cancelClose() {
    this.showConfirmationModal = false;
  }

  @action
  createWorkflow() {
    return taskFor(this.createWorkflowTask).perform();
  }

  @action
  updateWorkflow() {
    return taskFor(this.updateWorkflowTask).perform();
  }

  @action
  deleteWorkflow() {
    return taskFor(this.deleteWorkflowTask).perform();
  }

  @task({ restartable: true })
  *fetchWorkflowTask(): TaskGenerator<void> {
    let data = yield get(
      this._workflowApiUrl(this.args.routingOutcomeIdentifier, 'show_workflow'),
      {
        app_id: this.appService.app.id,
      },
    );

    this.workflow = this.store.push(
      this.store.normalize('operator/visual-builder/workflow', data),
    ) as Workflow;

    // Store the initial workflow state to detect if it's empty
    this.initialWorkflowState = JSON.stringify(this.workflow.serialize());
  }

  @task({ restartable: true })
  *createWorkflowTask(): TaskGenerator<void> {
    yield post(this._workflowApiUrl(this.args.routingOutcomeIdentifier, 'create_workflow'), {
      app_id: this.appService.app.id,
    });
  }

  @task({ restartable: true })
  *updateWorkflowTask(): TaskGenerator<void> {
    yield put(this._workflowApiUrl(this.args.routingOutcomeIdentifier, 'update_workflow'), {
      app_id: this.appService.app.id,
      workflow: this.workflow?.serialize(),
    });
    this.args.onClose();
  }

  @task({ restartable: true })
  *deleteWorkflowTask(): TaskGenerator<void> {
    yield ajaxDelete(this._workflowApiUrl(this.args.routingOutcomeIdentifier, 'destroy_workflow'), {
      app_id: this.appService.app.id,
    });
    // Notify React to update form state so the button changes from "Edit" to "Add"
    this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
      identifier: 'fin-sdr:workflow-deleted',
      data: { routingOutcomeIdentifier: this.args.routingOutcomeIdentifier },
    });
    this.args.onClose();
  }

  private _workflowApiUrl(
    routingOutcomeIdentifier: string,
    controllerMethod: 'show_workflow' | 'create_workflow' | 'update_workflow' | 'destroy_workflow',
  ) {
    return `/teammate_app/fin_sdr_config/routing_outcomes/${routingOutcomeIdentifier}/${controllerMethod}`;
  }

  private _isEmptyWorkflow(): boolean {
    if (!this.workflow || !this.initialWorkflowState) {
      return false;
    }

    // Check if the initial state was an empty workflow (no steps)
    // Even empty workflows have at least one group for the empty state
    let initialState = JSON.parse(this.initialWorkflowState);

    // Check if there are no groups, or if all groups have no steps
    if (!initialState.groups || initialState.groups.length === 0) {
      return true;
    }

    // Check if all groups are empty (no steps)
    return initialState.groups.every((group: any) => !group.steps || group.steps.length === 0);
  }

  private _shouldDeleteAfterRollback(): boolean {
    // Delete if it started empty and has unsaved changes
    return this._isEmptyWorkflow() && this.hasUnsavedChanges;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::SalesDevelopment::WorkflowSideSheet': typeof SalesDevelopmentWorkflowSideSheet;
    'ai-agent/sales-development/workflow-side-sheet': typeof SalesDevelopmentWorkflowSideSheet;
  }
}
